/**
 * Minhas páginas — a grade.
 *
 * Projetos de página HTML publicados pela própria pessoa: protótipos, provas de
 * conceito, telas para discutir numa reunião. O conteúdo é autocontido e com
 * dados fictícios.
 *
 * ## Por que grade com amostra, e não lista de linhas
 *
 * O endereço de um projeto é `pagina-` mais 32 hexadecimais — ninguém decora, e
 * **o nome é a única coisa que distingue um projeto do outro numa lista de
 * texto**. Com cinco "Dashboard", "Dashboard v2", "Teste", a lista deixa de
 * ajudar: a pessoa abre um por um até achar.
 *
 * A amostra resolve isso porque protótipo se reconhece de relance. É a mesma
 * razão de um gerenciador de arquivos mostrar miniatura de imagem em vez do
 * nome do arquivo.
 *
 * ## A amostra é a página de verdade, rodando
 *
 * Não é imagem: é um `iframe` do próprio conteúdo, reduzido. Não há serviço de
 * captura de tela aqui, e gerar imagem no servidor pediria um navegador sem
 * cabeça — infraestrutura inteira para uma miniatura.
 *
 * O que isso custa, e por que está sob controle: cada cartão carrega a página
 * de verdade. `loading="lazy"` segura os que estão fora da tela, e a cota de 20
 * projetos por pessoa limita o pior caso. O quadro não recebe clique
 * (`pointer-events: none` no CSS) — quem clica está mirando o cartão.
 *
 * E a contenção é a mesma da página publicada: `sandbox` sem
 * `allow-same-origin`, conteúdo servido em origem opaca. Uma miniatura não é
 * motivo para afrouxar nada.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { AppShell, Banner, StatsBar } from '../components/AppShell.jsx';
import { Toast, notify } from '../components/Toast.jsx';
import { ConectorDialog } from '../components/ConectorDialog.jsx';
import { api, SessionError, goToSignIn } from '../api.js';
import { RostosDoCartao } from '../components/Rostos.jsx';

/** Rótulo e cor de cada visibilidade. Usa as pastilhas que já existem. */
/**
 * Os três estados de alcance, desde 21/10.
 *
 * `PUBLICO` e `INTERNO` saíram. O primeiro era o único alcance que ia além da
 * Iguatemi e não tinha nenhum artefato usando; o segundo virou `HUB`, a mesma
 * regra com outro nome.
 *
 * **`COMPARTILHADO` não se escolhe.** Ele vale exatamente quando há alguém na
 * lista, e o servidor o recalcula a cada mudança — por isso ele não aparece
 * como opção em lugar nenhum da tela, só como estado.
 */
export const VISIBILIDADE = {
  PRIVADO: { label: 'Privado', cls: 'neutral', hint: 'Só você vê.' },
  COMPARTILHADO: {
    label: 'Compartilhado',
    cls: 'info',
    hint: 'Só as pessoas da lista veem.',
  },
  HUB: { label: 'Todo o AI Hub', cls: 'pending', hint: 'Qualquer pessoa com acesso ao AI Hub vê.' },
};

export const emMB = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const dataCurta = (iso) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * O endereço do arquivo de entrada, com um selo de versão.
 *
 * ## O prefixo vem do servidor, e não é montado aqui
 *
 * `contentBase` já traz o crachá de leitura quando o projeto não é público.
 * Ele é necessário porque o conteúdo roda em origem opaca, e de lá o navegador
 * **não manda o cookie de sessão** — sem o crachá, o CSS e o JS do protótipo
 * recebem um redirecionamento para o login no lugar do arquivo, e a página
 * aparece crua. Montar `${site.path}/conteudo/...` aqui reintroduziria o
 * defeito sem nenhum aviso.
 *
 * O `?v=` não chega ao servidor como filtro de nada — a rota de conteúdo ignora
 * a query. Ele existe para o NAVEGADOR: muda a URL e, com isso, o quadro
 * recarrega em vez de reaproveitar o que já desenhou.
 */
export const urlDoConteudo = (site, selo, base) =>
  // `base` sobrepõe o `contentBase` do projeto, e existe por causa do crachá:
  // em projeto não público ele é reassinado a cada leitura da API, então quem
  // mantém um quadro aberto precisa FIXAR o endereço em vez de reler o do
  // objeto. Ver `quadro` em `SiteDetail.jsx`. Sem o argumento, o comportamento
  // é o de sempre — que é o certo para a miniatura do cartão, desenhada uma
  // vez e descartada.
  `${base ?? site.contentBase}/${site.entryPath.split('/').map(encodeURIComponent).join('/')}` +
  `?v=${encodeURIComponent(selo ?? site.updatedAt ?? '')}`;

/* ── a amostra do cartão ─────────────────────────────────────── */

/**
 * As ordens possíveis.
 *
 * `recentes` primeiro porque é o que a lista já fazia e o que quase sempre se
 * quer: o artefato em que se mexeu por último é o que se vai abrir de novo.
 * As outras duas existem para quando a lista cresce — nome para procurar o que
 * se sabe que existe, tamanho para achar o que está pesando.
 *
 * `updatedAt` e não `createdAt`: o cartão mostra a data de criação, mas a
 * ordem útil é a da última mexida. São diferentes assim que alguém reenvia um
 * arquivo.
 */
const ORDENS = {
  recentes: {
    label: 'Mais recentes',
    cmp: (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
  },
  nome: {
    label: 'Nome (A–Z)',
    // `localeCompare` com `pt`: sem ele, "Área" cai depois de "Zebra" porque a
    // comparação vira byte a byte.
    cmp: (a, b) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }),
  },
  tamanho: {
    label: 'Maiores primeiro',
    cmp: (a, b) => (b.totalBytes ?? 0) - (a.totalBytes ?? 0),
  },
};

/** Normaliza para busca: sem acento e em minúsculas. */
const paraBusca = (texto) =>
  (texto ?? '')
    .toLowerCase()
    // Quem digita "operacoes" procurando "Painel operações" espera achar. Sem
    // isto a busca só funciona para quem acentua certo na pressa.
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

function Amostra({ site }) {
  const vazio = (site.fileCount ?? 0) === 0;

  if (vazio || site.entryMissing) {
    return (
      <div className="site-thumb is-empty">
        <span>{vazio ? 'Sem arquivos ainda' : 'Sem página de entrada'}</span>
      </div>
    );
  }

  return (
    <div className="site-thumb">
      <iframe
        className="site-thumb-frame"
        src={urlDoConteudo(site)}
        title=""
        // Fora da ordem de tabulação e escondido do leitor de tela: é
        // decoração. Quem navega por teclado pega o nome do projeto, que é um
        // link de verdade logo abaixo.
        tabIndex={-1}
        aria-hidden="true"
        loading="lazy"
        scrolling="no"
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}

/* ── criar ───────────────────────────────────────────────────── */

/**
 * Criar projeto, numa janela.
 *
 * Era um formulário em linha, dentro do cabeçalho da lista — e ficava espremido
 * numa coluna estreita ao lado dos projetos, com o nome e a descrição apertados
 * um em cima do outro. Numa janela os dois campos ocupam a largura que pedem.
 */
function NovoProjeto({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Esc fecha. Sem isso a única saída é o mouse, e a janela cobre a tela.
  useEffect(() => {
    const aoTeclar = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  async function criar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const { site } = await api.sites.create({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      onCreated(site);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
      setSalvando(false);
    }
    return undefined;
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nova-pagina-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <form className="editor-form" onSubmit={criar}>
          <header className="panel-header editor-head">
            <h2 id="nova-pagina-titulo" className="panel-title">
              Novo artefato
            </h2>
            <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
              ×
            </button>
          </header>

          <div className="panel-body editor-body">
            <div className="field">
              <label className="field-label" htmlFor="site-nome">
                Nome
              </label>
              <input
                id="site-nome"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Protótipo do painel de vendas"
                maxLength={120}
                /* O foco automático é o certo numa janela que a pessoa acabou
                   de abrir com um clique: o cursor vai para onde ela pediu
                   para escrever. */
                autoFocus
                required
              />
              <p className="field-hint">Como você vai reconhecer este projeto na lista.</p>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="site-desc">
                Descrição <span className="optional">opcional</span>
              </label>
              <textarea
                id="site-desc"
                className="textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="O que é isto, e para quem."
                rows={3}
                maxLength={2000}
              />
              <p className="field-hint">
                Aparece na lista e na faixa de procedência da página publicada.
              </p>
            </div>
          </div>

          <footer className="panel-actions editor-head">
            <button
              type="submit"
              className="button primary"
              disabled={salvando || name.trim().length < 2}
            >
              {salvando ? 'Criando…' : 'Criar projeto'}
            </button>
            <button type="button" className="button secondary" onClick={onClose}>
              Cancelar
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

/* ── a tela ──────────────────────────────────────────────────── */

export function Sites() {
  usePageMeta('AI Hub — meus artefatos');

  const [list, setList] = useState(null);
  const [comigo, setComigo] = useState([]);
  const [doHub, setDoHub] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [conector, setConector] = useState(false);
  // `''` são os meus, `'COMIGO'` os que compartilharam comigo, e os demais
  // filtram os meus por alcance. Um estado só, porque as três coisas são
  // mutuamente exclusivas na tela.
  const [filtro, setFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState('recentes');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.sites.list();
      setList(r.sites);
      setComigo(r.compartilhados ?? []);
      setDoHub(r.doHub ?? []);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Quem não publicou nada cai direto nos abertos a todos.
   *
   * ## O que isto conserta (26/10)
   *
   * A lista mostra, por padrão, só os SEUS artefatos. Para quem nunca publicou,
   * isso era uma tela vazia — mesmo havendo artefatos abertos a todo o AI Hub,
   * que ficavam atrás de uma aba que ninguém clica no primeiro acesso.
   *
   * Dois efeitos ruins, e o segundo é o que motivou a correção:
   *
   *   1. a pessoa conclui que não há nada para ver, quando há;
   *   2. **o tour de artefatos perde quatro dos seis passos.** Eles dependem de
   *      haver um cartão na tela (`temArtefato` é a existência da âncora
   *      `artefatos-cartao`), e sem cartão o tour pula direto para o fim — logo
   *      no primeiro acesso, que é exatamente quando alguém faz o tour.
   *
   * Só no primeiro carregamento e só se não houver nada seu: depois disso a
   * escolha é de quem está usando, e trocar a aba por baixo seria pior do que
   * a tela vazia.
   */
  const jaEscolhi = useRef(false);
  useEffect(() => {
    if (jaEscolhi.current || loading || list === null) return;
    jaEscolhi.current = true;
    if (list.length === 0 && comigo.length === 0 && doHub.length > 0) setFiltro('DO_HUB');
  }, [loading, list, comigo, doHub]);

  const publicados = (list ?? []).filter((s) => s.visibility !== 'PRIVADO').length;

  /** Quantos há de cada visibilidade. Vira o número na pastilha do filtro. */
  const contagem = useMemo(() => {
    const c = {};
    for (const s of list ?? []) c[s.visibility] = (c[s.visibility] ?? 0) + 1;
    return c;
  }, [list]);

  /**
   * Só as visibilidades que existem viram pastilha.
   *
   * Mostrar as três sempre deixaria duas pastilhas zeradas numa lista de
   * quatro artefatos — ruído que não filtra nada. Elas aparecem sozinhas
   * quando a primeira pública for publicada, e some de novo quando não houver.
   */
  const filtros = useMemo(
    () => Object.keys(VISIBILIDADE).filter((v) => contagem[v] > 0),
    [contagem],
  );

  const visiveis = useMemo(() => {
    const termo = paraBusca(busca.trim());
    // A lista de base muda com o filtro: as duas abas de fora não são recortes
    // dos meus artefatos, são outros conjuntos.
    //
    // `DO_HUB` entrou em 22/10. A tela sempre prometeu que "Todo o AI Hub" era
    // visto por qualquer pessoa da casa, mas o artefato não aparecia em lista
    // nenhuma — só chegava a quem recebesse o link. Isto não abre acesso: o
    // servidor já deixava qualquer pessoa autenticada abrir.
    const OUTRAS = { COMIGO: comigo, DO_HUB: doHub };
    const base = OUTRAS[filtro] ?? list ?? [];
    return (
      base
        .filter((s) => OUTRAS[filtro] || !filtro || s.visibility === filtro)
        .filter(
          (s) =>
            !termo || paraBusca(s.name).includes(termo) || paraBusca(s.description).includes(termo),
        )
        // Cópia antes de ordenar: `sort` muda o array no lugar, e `list` é o
        // estado — reordenar nele faria o React não ver mudança nenhuma e a
        // tela ficaria na ordem antiga até o próximo carregamento.
        .slice()
        .sort(ORDENS[ordem].cmp)
    );
  }, [list, comigo, doHub, filtro, busca, ordem]);

  // Sem `filtrando`: quando `list` tem itens e `visiveis` não tem, algum
  // filtro está necessariamente ligado — a condição já é a resposta.
  const limpar = () => {
    setFiltro('');
    setBusca('');
  };

  // Com um artefato só não há o que filtrar, e a barra seria enfeite ocupando
  // a linha em que o único cartão deveria estar.
  // A barra aparece também quando há só um artefato meu — ou nenhum — mas
  // existe algo dos outros para ver: sem ela, a aba que o mostraria não teria
  // onde aparecer, e a pessoa ficaria com uma tela vazia tendo o que olhar.
  const mostrarFiltros = (list?.length ?? 0) > 1 || comigo.length > 0 || doHub.length > 0;

  return (
    <AppShell>
      <Banner title="Meus artefatos">
        Publique um artefato HTML autocontido e compartilhe o endereço.
      </Banner>

      <StatsBar
        items={[
          { label: 'Artefatos', value: list?.length ?? '—' },
          { label: 'Compartilhados', value: list ? publicados : '—' },
        ]}
      />

      <div className="site-toolbar">
        <span className="section-label">Artefatos</span>
        <div className="site-toolbar-acoes" data-tour="artefatos-criar">
          {/* Configuração de uma vez por aparelho: botão discreto, não compete
              com a ação principal da tela. */}
          <button type="button" className="button secondary" onClick={() => setConector(true)}>
            Publicar do Claude
          </button>
          <button type="button" className="button primary" onClick={() => setCriando(true)}>
            Novo artefato
          </button>
        </div>
      </div>

      {!loading && !error && mostrarFiltros && (
        <div className="tabs-row site-filtros">
          <div
            className="tabs"
            role="tablist"
            aria-label="Filtrar por quem pode ver"
            data-tour="artefatos-visibilidade"
          >
            <button
              type="button"
              role="tab"
              aria-selected={filtro === ''}
              className={`tab ${filtro === '' ? 'is-active' : ''}`}
              onClick={() => setFiltro('')}
            >
              Todos
              <span className="tab-badge">{list.length}</span>
            </button>

            {comigo.length > 0 && (
              /* Fora do laço das visibilidades de propósito: não é um alcance
                 dos meus artefatos, é outra lista — e por isso leva a marca
                 que o CSS usa para separá-la da fileira. */
              <button
                type="button"
                role="tab"
                aria-selected={filtro === 'COMIGO'}
                className={`tab is-outra-lista ${filtro === 'COMIGO' ? 'is-active' : ''}`}
                onClick={() => setFiltro('COMIGO')}
              >
                Compartilhados comigo
                <span className="tab-badge">{comigo.length}</span>
              </button>
            )}

            {doHub.length > 0 && (
              /* Pelo mesmo motivo da de cima: é outra lista, não um alcance
                 dos meus artefatos. E separada de "Compartilhados comigo"
                 porque a diferença importa — lá alguém escolheu VOCÊ; aqui
                 alguém não escolheu ninguém. */
              <button
                type="button"
                role="tab"
                aria-selected={filtro === 'DO_HUB'}
                className={`tab is-outra-lista ${filtro === 'DO_HUB' ? 'is-active' : ''}`}
                onClick={() => setFiltro('DO_HUB')}
              >
                Abertos a todos
                <span className="tab-badge">{doHub.length}</span>
              </button>
            )}

            {filtros.map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={filtro === v}
                className={`tab ${filtro === v ? 'is-active' : ''}`}
                onClick={() => setFiltro(v)}
              >
                {VISIBILIDADE[v].label}
                <span className="tab-badge">{contagem[v]}</span>
              </button>
            ))}
          </div>

          <div className="tabs-actions">
            <input
              className="search-box"
              type="search"
              placeholder="Buscar por nome"
              aria-label="Buscar artefato por nome"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />

            <label className="site-ordem">
              <span className="visually-hidden">Ordenar por</span>
              <select className="input" value={ordem} onChange={(e) => setOrdem(e.target.value)}>
                {Object.entries(ORDENS).map(([id, o]) => (
                  <option key={id} value={id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {loading && <p className="state-message">Carregando…</p>}

      {error && (
        <div className="state-message error" role="alert">
          <p>{error}</p>
          <button className="button secondary" type="button" onClick={load}>
            Tentar de novo
          </button>
        </div>
      )}

      {/* `doHub` entra na condição: dizer "nenhum artefato publicado ainda"
          tendo artefatos abertos a todos na aba ao lado é contradizer a
          própria tela. Nesse caso a aba já veio selecionada, acima. */}
      {!loading && !error && list?.length === 0 && comigo.length === 0 && doHub.length === 0 && (
        <div className="site-empty">
          <h2>Nenhum artefato publicado ainda</h2>
          <p>
            Serve para protótipo, prova de conceito ou uma tela para mostrar numa reunião — HTML,
            CSS, JS e imagens, com dados fictícios.
          </p>
          <button type="button" className="button primary" onClick={() => setCriando(true)}>
            Criar a primeira
          </button>
        </div>
      )}

      {/* Filtrou e não sobrou nada. É diferente de "não há artefato nenhum",
          que é o estado acima — aqui existe o que ver, só não com estes
          filtros, e o caminho de volta tem de estar à mão. */}
      {!loading && !error && list?.length > 0 && visiveis.length === 0 && (
        <p className="state-message">
          Nenhum artefato corresponde ao que você buscou.{' '}
          <button type="button" className="button ghost clear-filters" onClick={limpar}>
            Limpar filtros
          </button>
        </p>
      )}

      {!loading && !error && visiveis.length > 0 && (
        <div className="site-grid">
          {visiveis.map((s, i) => {
            const v = VISIBILIDADE[s.visibility] ?? { label: s.visibility, cls: 'neutral' };
            return (
              // `data-tour` só no primeiro: `querySelector` pega o primeiro de
              // qualquer jeito, e marcar os vinte faria parecer que a âncora é
              // "todo cartão" para quem for mexer depois.
              <article
                className="site-card"
                key={s.id}
                data-tour={i === 0 ? 'artefatos-cartao' : undefined}
              >
                <Amostra site={s} />

                <div className="site-card-body">
                  <div className="site-card-head">
                    {/* O link cobre o cartão inteiro por `::after` no CSS, mas
                        o alvo clicável de verdade é este — é ele que o leitor
                        de tela anuncia, com o nome do projeto. */}
                    {/* Artefato de outra pessoa não tem tela de edição para
                        abrir — `/artefatos/:id` responde 404 para quem não é
                        dono. O cartão leva ao conteúdo publicado, que é o que
                        quem recebeu veio ver. */}
                    {s.ownerUpn ? (
                      <a className="site-card-name" href={s.path} target="_blank" rel="noreferrer">
                        {s.name}
                      </a>
                    ) : (
                      <Link className="site-card-name" to={`/artefatos/${s.id}`}>
                        {s.name}
                      </Link>
                    )}
                    <span className={`status-pill ${v.cls}`}>{v.label}</span>
                  </div>

                  {s.ownerUpn && <p className="site-card-dono">de {s.ownerName ?? s.ownerUpn}</p>}

                  {s.description && <p className="site-card-desc">{s.description}</p>}

                  <div className="site-card-foot">
                    <span>
                      {s.fileCount === 0
                        ? 'Nenhum arquivo'
                        : `${s.fileCount} arquivo${s.fileCount > 1 ? 's' : ''} · ${emMB(s.totalBytes)}`}
                    </span>
                    <span>{dataCurta(s.createdAt)}</span>
                  </div>

                  {/* Com quem está compartilhado. Só nos meus: no cartão do
                      que recebi, "de quem" já está acima e a lista de quem
                      mais recebeu não é da minha conta. */}
                  {!s.ownerUpn && s.shareCount > 0 && (
                    <div className="site-card-rostos">
                      <RostosDoCartao lista={s.shares} total={s.shareCount} />
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {criando && (
        <NovoProjeto
          onClose={() => setCriando(false)}
          /* Criar abre o artefato direto, na tela de envio.
             Antes voltava para a grade, e a pessoa tinha de achar o cartão
             recém-criado e clicar nele para fazer a única coisa que faltava —
             um passo que não decidia nada. */
          onCreated={(site) => {
            setCriando(false);
            navigate(`/artefatos/${site.id}`);
          }}
        />
      )}

      {/* Ao fechar, relê a lista: o Claude pode ter publicado enquanto a
          janela estava aberta. */}
      {conector && (
        <ConectorDialog
          onClose={() => {
            setConector(false);
            load();
          }}
        />
      )}

      <Toast />
    </AppShell>
  );
}
